/**
 * The app's editable model: a `Draft`.
 *
 * Deliberately distinct from the wire `DeviceConfig`. The wire type is shaped by
 * what the device can represent (seconds, no steps); the draft is shaped by what a
 * person is editing (milliseconds, an explicit step list, validation feedback).
 * Converting between them is `draftToDevice` / `draftFromDevice` and happens only
 * at the boundaries — push to device, and read back from device.
 *
 * Keeps errors as data rather than exceptions so the UI can show them inline.
 */

import {
  CFG_BOOT_DELAY_DEFAULT_MS,
  CFG_BOOT_DELAY_MAX_MS,
  CFG_MAX_AMPLITUDE,
  CFG_MAX_STEPS,
  CFG_MS_PER_SEC,
  PatternKind,
} from '../protocol/constants.ts';
import { MAX_DELTA, maxSafeAmplitude, maxStepDelta } from './generators.ts';
import type { PatternKindValue } from '../protocol/types.ts';
import type { DeviceConfig, JigStep } from '../protocol/types.ts';

export interface Draft {
  kind: PatternKindValue;
  /** Milliseconds. 0 means "run until power-off". */
  durationMs: number;
  bootDelayMs: number;
  /** Generator parameters. Ignored for MACRO. */
  amplitude: number;
  periodMs: number;
  jitter: number;
  seed: number;
  flags: number;
  steps: JigStep[];
}

/* ------------------------------------------------------------- factories -- */

export function emptyStep(): JigStep {
  return { dx: 0, dy: 0, wheel: 0, buttons: 0, delayMs: 50 };
}

/**
 * The compiled-in default, mirroring Config_Defaults() in Config.h.
 *
 * Kept in step with the firmware because the UI shows it as "what the device does
 * with no stored config". If the two disagree the user sees a lie.
 */
export function defaultDraft(): Draft {
  return {
    kind: PatternKind.MACRO,
    durationMs: 8 * 60 * 60 * 1000,
    bootDelayMs: CFG_BOOT_DELAY_DEFAULT_MS,
    amplitude: 0,
    periodMs: 0,
    jitter: 0,
    seed: 0x5a,
    flags: 0,
    steps: [
      { dx: 1, dy: 0, wheel: 0, buttons: 0, delayMs: 30_000 },
      { dx: -1, dy: 0, wheel: 0, buttons: 0, delayMs: 30_000 },
    ],
  };
}

/** A reasonable starting point for a new macro: something visible but not violent. */
export function newMacroDraft(): Draft {
  return {
    kind: PatternKind.MACRO,
    durationMs: 8 * 60 * 60 * 1000,
    bootDelayMs: CFG_BOOT_DELAY_DEFAULT_MS,
    amplitude: 0,
    periodMs: 0,
    jitter: 0,
    seed: 0x5a,
    flags: 0,
    steps: [
      { dx: 5, dy: 0, wheel: 0, buttons: 0, delayMs: 1000 },
      { dx: -5, dy: 0, wheel: 0, buttons: 0, delayMs: 1000 },
    ],
  };
}

export function generatorDraft(kind: PatternKindValue): Draft {
  return {
    kind,
    durationMs: 8 * 60 * 60 * 1000,
    bootDelayMs: CFG_BOOT_DELAY_DEFAULT_MS,
    amplitude: 20,
    periodMs: 10_000,
    jitter: 1,
    seed: 0x5a,
    flags: 0,
    steps: [],
  };
}

/* ----------------------------------------------------------- conversions -- */

export function draftToDevice(draft: Draft): DeviceConfig {
  return {
    kind: draft.kind,
    flags: draft.flags,
    jitter: draft.jitter,
    seed: draft.seed,
    durationMs: draft.durationMs,
    amplitude: draft.amplitude,
    periodMs: draft.periodMs,
    stepCount: draft.kind === PatternKind.MACRO ? draft.steps.length : 0,
    bootDelayMs: draft.bootDelayMs,
    saved: false,
  };
}

export function draftFromDevice(cfg: DeviceConfig, steps: JigStep[]): Draft {
  return {
    kind: cfg.kind,
    durationMs: cfg.durationMs,
    bootDelayMs: cfg.bootDelayMs,
    amplitude: cfg.amplitude,
    periodMs: cfg.periodMs,
    jitter: cfg.jitter,
    seed: cfg.seed,
    flags: cfg.flags,
    steps: cfg.kind === PatternKind.MACRO ? steps : [],
  };
}

/* ------------------------------------------------------------ validation -- */

export interface Problem {
  field: keyof Draft | 'steps' | `steps.${number}`;
  message: string;
}

/**
 * Problems that would make the device NACK, or that would silently do the wrong
 * thing. Each one mirrors an actual firmware check in Protocol_UnpackConfig() or a
 * documented limit, so the UI can refuse locally with a useful message instead of
 * surfacing a bare NACK reason byte.
 */
export function validateDraft(draft: Draft): Problem[] {
  const problems: Problem[] = [];

  if (draft.durationMs < 0) {
    problems.push({ field: 'durationMs', message: 'Duration cannot be negative.' });
  }
  const durationS = Math.ceil(draft.durationMs / CFG_MS_PER_SEC);
  if (durationS > 0xffff) {
    problems.push({
      field: 'durationMs',
      message: `Duration ${durationS} s exceeds the device limit of 65535 s (about 18 hours).`,
    });
  }

  if (draft.bootDelayMs < 0 || draft.bootDelayMs > CFG_BOOT_DELAY_MAX_MS) {
    problems.push({
      field: 'bootDelayMs',
      message: `Boot delay must be 0-${CFG_BOOT_DELAY_MAX_MS} ms. 0 removes the safety window and is racy.`,
    });
  }

  if (draft.amplitude < 0 || draft.amplitude > CFG_MAX_AMPLITUDE) {
    problems.push({
      field: 'amplitude',
      message: `Amplitude must be 0-${CFG_MAX_AMPLITUDE} px.`,
    });
  }

  if (draft.jitter < 0 || draft.jitter > 255) {
    problems.push({ field: 'jitter', message: 'Jitter must be 0-255.' });
  }

  /*
   * The constraint that ACTUALLY matters for a generator, expressed in terms of the
   * thing that actually matters: a mouse report carries dx/dy as int8, so no single
   * step may ask for more than 127 px on an axis.
   *
   * Amplitude is a position bound, not a delta -- the per-step motion is a fraction of
   * it (about 9.8% for CIRCLE, 6.25% for the linear generators). Checking amplitude
   * against 127 was a 10x-too-conservative conflation of the two, and the derived
   * check below is what makes the real ceiling enforceable instead.
   */
  if (draft.kind !== PatternKind.MACRO) {
    const delta = maxStepDelta(draft.kind, draft.amplitude, draft.jitter);
    if (delta > MAX_DELTA) {
      const safe = maxSafeAmplitude(draft.kind);
      problems.push({
        field: 'amplitude',
        message:
          `Amplitude ${draft.amplitude} asks for a ${delta} px step, and a mouse report carries at most ${MAX_DELTA} px. ` +
          (Number.isFinite(safe)
            ? `Use ${safe} or less for this pattern kind.`
            : 'Reduce the jitter.'),
      });
    }
  }

  if (draft.kind === PatternKind.MACRO) {
    if (draft.steps.length === 0) {
      problems.push({ field: 'steps', message: 'A macro needs at least one step, or the device emits nothing.' });
    }
    if (draft.steps.length > CFG_MAX_STEPS) {
      problems.push({
        field: 'steps',
        message: `${draft.steps.length} steps exceeds the device limit of ${CFG_MAX_STEPS}.`,
      });
    }
    draft.steps.forEach((step, i) => {
      if (!inDeltaRange(step.dx) || !inDeltaRange(step.dy) || !inDeltaRange(step.wheel)) {
        problems.push({
          field: `steps.${i}`,
          message: `Step ${i + 1}: dx, dy and wheel must each be -127 to 127. Split a larger move into several steps.`,
        });
      }
      if (step.delayMs < 0 || step.delayMs > 0xffff) {
        problems.push({ field: `steps.${i}`, message: `Step ${i + 1}: delay must be 0-65535 ms.` });
      }
      if (step.buttons < 0 || step.buttons > 7) {
        problems.push({ field: `steps.${i}`, message: `Step ${i + 1}: buttons must be 0-7.` });
      }
    });
  }

  return problems;
}

function inDeltaRange(v: number): boolean {
  return Number.isInteger(v) && v >= -127 && v <= 127;
}

export function isValid(draft: Draft): boolean {
  return validateDraft(draft).length === 0;
}

/* ---------------------------------------------------------------- helpers -- */

/** Movement speed alone says nothing about the risk of clicks or held buttons. */
export function hasButtonActions(draft: Draft): boolean {
  return draft.kind === PatternKind.MACRO && draft.steps.some((step) => step.buttons !== 0);
}

/** Compare device-visible configuration, excluding a generator's retained editor-only steps. */
export function draftMatchesDevice(draft: Draft, device: Draft): boolean {
  return draftsEqual(
    draft.kind === PatternKind.MACRO ? draft : { ...draft, steps: [] },
    device.kind === PatternKind.MACRO ? device : { ...device, steps: [] },
  );
}

/** Exact equality, including inactive steps retained for authoring/profile storage. */
export function draftsEqual(a: Draft, b: Draft): boolean {
  if (
    a.kind !== b.kind ||
    a.durationMs !== b.durationMs ||
    a.bootDelayMs !== b.bootDelayMs ||
    a.amplitude !== b.amplitude ||
    a.periodMs !== b.periodMs ||
    a.jitter !== b.jitter ||
    a.seed !== b.seed ||
    a.flags !== b.flags ||
    a.steps.length !== b.steps.length
  ) {
    return false;
  }
  return a.steps.every((s, i) => {
    const o = b.steps[i];
    return (
      o !== undefined &&
      s.dx === o.dx &&
      s.dy === o.dy &&
      s.wheel === o.wheel &&
      s.buttons === o.buttons &&
      s.delayMs === o.delayMs
    );
  });
}
